import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import AnalogClockTimePicker from "./AnalogClockTimePicker";
import { ensureFormUuid } from "../../utils/formUuid";
import { normalizeImageSource } from "../../shared-pdf/helpers";
import {
  buildPdf as buildHotWorksPdf,
  buildFileName as hotWorksFileName,
  meta as hotWorksMeta,
  COMPLETION_STATEMENT,
  PERMIT_NOTE,
  PRECAUTION_GROUPS,
} from "../../shared-pdf/forms/hotWorksPermit";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

const ANSWER_OPTIONS = ["", "Yes", "No", "N/A"];

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const d = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const GRID_LABEL = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4",
  verticalAlign: "middle",
};
const GRID_FIELD = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "6px 8px",
  background: "#fff",
  verticalAlign: "middle",
};
const SECTION_BAR = {
  background: THEME,
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  padding: "8px 10px",
};

function renderSignatureBox(value) {
  return (
    <div
      style={{
        border: "1px dashed #8b1634",
        borderRadius: 8,
        background: "#fbeef2",
        minHeight: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      {value ? (
        <img
          src={normalizeImageSource(value, "image/png")}
          alt="Signature"
          style={{ maxWidth: "100%", maxHeight: 48, objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <span style={{ color: "#999", fontSize: 11 }}>No signature</span>
      )}
    </div>
  );
}

function ThemedSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", color: value ? "#2d3748" : "#94a3b8", fontSize: 13, cursor: "pointer" }}
      >
        <span>{value || "—"}</span>
        <i className="fa fa-chevron-down" style={{ fontSize: 9, color: THEME }} />
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "#fff", border: `1px solid ${THEME}`, borderRadius: 8, overflow: "hidden", boxShadow: "0 6px 16px rgba(0,0,0,0.14)" }}>
          {options.map((o) => (
            <div
              key={o || "blank"}
              onClick={() => { onChange(o); setOpen(false); }}
              style={{ padding: "7px 12px", fontSize: 13, cursor: "pointer", color: value === o ? "#fff" : THEME, background: value === o ? THEME : "#fff", fontWeight: value === o ? 600 : 400 }}
              onMouseEnter={(e) => { if (value !== o) e.currentTarget.style.background = "#fdf2f6"; }}
              onMouseLeave={(e) => { if (value !== o) e.currentTarget.style.background = "#fff"; }}
            >
              {o || "—"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HotWorksPermitPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/hot-works-permit", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [r.siteName, r.locationOfHotWork, r.natureOfHotWork, r.preparedName, r.authName, r.issuedTo]
      .map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((f) => f.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    const named = `${record?.addedByFirstName || ""} ${record?.addedByLastName || ""}`.trim();
    return named || uid || "—";
  };

  const handleEdit = (r) => {
    setEditForm({ ...r, precautions: r.precautions && typeof r.precautions === "object" ? { ...r.precautions } : {}, formUuid: ensureFormUuid(r?.formUuid) });
    setShowEditModal(true);
  };

  const updatePrecaution = (key, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      precautions: { ...(prev.precautions || {}), [key]: { ...((prev.precautions || {})[key] || {}), [field]: value } },
    }));
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/hot-works-permit/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          siteName: editForm.siteName,
          locationOfHotWork: editForm.locationOfHotWork,
          natureOfHotWork: editForm.natureOfHotWork,
          precautions: editForm.precautions || {},
          preparedName: editForm.preparedName,
          preparedDate: editForm.preparedDate,
          preparedPosition: editForm.preparedPosition,
          issuedTo: editForm.issuedTo,
          timeOfIssue: editForm.timeOfIssue,
          timeOfExpiry: editForm.timeOfExpiry,
          authName: editForm.authName,
          authDate: editForm.authDate,
          authPosition: editForm.authPosition,
          timeAreaInspectionCompleted: editForm.timeAreaInspectionCompleted,
          completionName: editForm.completionName,
          completionDate: editForm.completionDate,
          completionPosition: editForm.completionPosition,
          cancelledName: editForm.cancelledName,
          cancelledDate: editForm.cancelledDate,
          cancelledPosition: editForm.cancelledPosition,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/hot-works-permit/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Layout lives in src/shared-pdf/forms/hotWorksPermit.js so the auto-sync
  // worker renders the identical document server-side. Only delivery (save or
  // upload) belongs here.
  const generatePDF = async (record, options = {}) => {
    const doc = await buildHotWorksPdf(record);
    const shouldUpload = options.uploadOnly === true;
    const fileName = hotWorksFileName(record, { forUpload: shouldUpload });

    if (!shouldUpload) {
      doc.save(fileName);
      toast.success("SF-029 Hot Works Permit PDF generated!");
      return;
    }

    try {
      setUploadingKey(record._id || "");
      const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({
          fileName,
          fileBase64: doc.output("datauristring"),
          uploadContext: hotWorksMeta.uploadContext,
          uploadSubFolder: hotWorksMeta.uploadSubFolder,
        }),
      });
      if (res.status !== 204 && !res.ok) {
        let msg = "SharePoint upload failed";
        try { const out = await res.json(); msg = out?.message || msg; } catch {}
        throw new Error(msg);
      }
      toast.success("PDF uploaded to SharePoint successfully!");
    } catch (err) {
      toast.error(err?.message || "SharePoint upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-029 Hot Works Permit</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site/Substation</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Nature of Hot Work</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => (
                  <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.siteName || "—"}</td>
                    <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.locationOfHotWork || "—"}</td>
                    <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.natureOfHotWork || "—"}</td>
                    <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                    <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.updatedAt || r.createdAt)}</td>
                    <td style={{ padding: 16 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {canEditDelete && (
                          <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                          </button>
                        )}
                        <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                          <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                        </button>
                        {(() => {
                          const busy = uploadingKey === (r._id || "");
                          return (
                            <button
                              type="button"
                              onClick={() => { if (!busy) generatePDF(r, { uploadOnly: true }); }}
                              disabled={busy}
                              title={busy ? "Uploading..." : "Upload to SharePoint"}
                              style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                            >
                              <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          );
                        })()}
                        {canEditDelete && (
                          <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1040, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-029 Hot Works Permit</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>

                {/* A. HOT WORK PREPARATION */}
                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>A. HOT WORK PREPARATION<div style={{ fontWeight: 400, fontSize: 11 }}>To be completed by competent operative or person responsible for carrying out the work</div></div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "32%" }}>Site/Substation Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.siteName || ""} onChange={(e) => setEditForm({ ...editForm, siteName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Location of hot work:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.locationOfHotWork || ""} onChange={(e) => setEditForm({ ...editForm, locationOfHotWork: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Nature of HOT WORK (ARC, Oxy-fuel, Grinding etc):</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.natureOfHotWork || ""} onChange={(e) => setEditForm({ ...editForm, natureOfHotWork: e.target.value })} /></td>
                      </tr>
                    </tbody>
                  </table>

                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "52%" }}>Safe Systems – Precautionary Actions</th>
                        <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "16%", textAlign: "center" }}>Yes/No/N/A</th>
                        <th style={{ ...GRID_LABEL, background: THEME, color: "#fff" }}>Specific Controls Put in Place</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PRECAUTION_GROUPS.map((g) => (
                        <React.Fragment key={g.title}>
                          <tr><td colSpan={3} style={{ ...GRID_LABEL, background: "#efe2e7" }}>{g.title}</td></tr>
                          {g.items.map((it) => {
                            const cur = (editForm.precautions || {})[it.key] || {};
                            return (
                              <tr key={it.key}>
                                <td style={{ ...GRID_FIELD, color: "#2d3748", fontWeight: 400 }}>{it.label}</td>
                                <td style={GRID_FIELD}>
                                  <ThemedSelect value={cur.answer || ""} onChange={(v) => updatePrecaution(it.key, "answer", v)} options={ANSWER_OPTIONS} />
                                </td>
                                <td style={GRID_FIELD}><input className="form-control form-control-sm" value={cur.controls || ""} onChange={(e) => updatePrecaution(it.key, "controls", e.target.value)} /></td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>Signed:</td>
                        <td style={{ ...GRID_FIELD, width: "34%" }}>{renderSignatureBox(editForm.preparedSigned)}</td>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.preparedName || ""} onChange={(e) => setEditForm({ ...editForm, preparedName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date:</td>
                        <td style={GRID_FIELD}>
                          <DatePicker selected={parseStoredDate(editForm.preparedDate)} onChange={(d) => setEditForm({ ...editForm, preparedDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" />
                        </td>
                        <td style={GRID_LABEL}>Position:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.preparedPosition || ""} onChange={(e) => setEditForm({ ...editForm, preparedPosition: e.target.value })} /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* B. AUTHORISATION */}
                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>B. HOT WORK AUTHORISATION to Commence<div style={{ fontWeight: 400, fontSize: 11 }}>To be completed by the Principal Contractor or Authorised Person</div></div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>This permit is issued to:</td>
                        <td style={GRID_FIELD} colSpan={3}>
                          <input className="form-control form-control-sm" placeholder="add name(s)" value={editForm.issuedTo || ""} onChange={(e) => setEditForm({ ...editForm, issuedTo: e.target.value })} />
                          <div style={{ fontSize: 11, color: "#718096", marginTop: 3 }}>subject to the controls above being put in place and a fire watch being completed.</div>
                        </td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Time of issue:</td>
                        <td style={{ ...GRID_FIELD, width: "34%" }}><AnalogClockTimePicker id="hwp-issue" value={editForm.timeOfIssue || ""} onChange={(t) => setEditForm({ ...editForm, timeOfIssue: t })} isClearable placeholder="Select time" /></td>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>Time of expiry:</td>
                        <td style={GRID_FIELD}><AnalogClockTimePicker id="hwp-expiry" value={editForm.timeOfExpiry || ""} onChange={(t) => setEditForm({ ...editForm, timeOfExpiry: t })} isClearable placeholder="Select time" /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Signed:</td>
                        <td style={GRID_FIELD}>{renderSignatureBox(editForm.authSigned)}</td>
                        <td style={GRID_LABEL}>Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.authName || ""} onChange={(e) => setEditForm({ ...editForm, authName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.authDate)} onChange={(d) => setEditForm({ ...editForm, authDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                        <td style={GRID_LABEL}>Position:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.authPosition || ""} onChange={(e) => setEditForm({ ...editForm, authPosition: e.target.value })} /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* C. COMPLETION */}
                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden" }}>
                  <div style={SECTION_BAR}>C. HOT WORK COMPLETION and FIRE WATCH<div style={{ fontWeight: 400, fontSize: 11 }}>To be completed by a trained welding operator or contractor responsible for the work</div></div>
                  <div style={{ padding: "8px 10px", fontSize: 11.5, color: "#2d3748", background: "#fff", borderBottom: "1px solid #cbd5e1" }}>{COMPLETION_STATEMENT}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "32%" }}>Time Area Inspection &amp; Fire Watch Completed:</td>
                        <td style={GRID_FIELD} colSpan={3}><AnalogClockTimePicker id="hwp-inspect" value={editForm.timeAreaInspectionCompleted || ""} onChange={(t) => setEditForm({ ...editForm, timeAreaInspectionCompleted: t })} isClearable placeholder="Select time" /></td>
                      </tr>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>Signed:</td>
                        <td style={{ ...GRID_FIELD, width: "34%" }}>{renderSignatureBox(editForm.completionSigned)}</td>
                        <td style={{ ...GRID_LABEL, width: "16%" }}>Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.completionName || ""} onChange={(e) => setEditForm({ ...editForm, completionName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.completionDate)} onChange={(d) => setEditForm({ ...editForm, completionDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                        <td style={GRID_LABEL}>Position:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.completionPosition || ""} onChange={(e) => setEditForm({ ...editForm, completionPosition: e.target.value })} /></td>
                      </tr>
                      <tr><td colSpan={4} style={{ ...GRID_LABEL, background: "#efe2e7" }}>Permit Cancelled by (Principal Contractor or Authorised Person):</td></tr>
                      <tr>
                        <td style={GRID_LABEL}>Signed:</td>
                        <td style={GRID_FIELD}>{renderSignatureBox(editForm.cancelledSigned)}</td>
                        <td style={GRID_LABEL}>Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.cancelledName || ""} onChange={(e) => setEditForm({ ...editForm, cancelledName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.cancelledDate)} onChange={(d) => setEditForm({ ...editForm, cancelledDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                        <td style={GRID_LABEL}>Position:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.cancelledPosition || ""} onChange={(e) => setEditForm({ ...editForm, cancelledPosition: e.target.value })} /></td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ padding: "8px 10px", fontSize: 11, fontStyle: "italic", color: "#92400e", background: "#fff" }}>{PERMIT_NOTE}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-029 Hot Works Permit File"
        importPath="hot-works-permit/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-029 Hot Works Permit</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
